// Holding pattern geometry helpers — convert FAA NASR / CIFP hold metadata
// into GeoJSON line/polygon coordinates suitable for MapLibre overlay AND
// SVG plate-render rendering.
//
// Holding pattern (racetrack) anatomy:
//
//                   ┌────────────────┐  outbound leg
//                   │                │
//                   │                │
//   turn 2 ────────►│                │◄──── turn 1
//      (180°)       │                │      (180°)
//                   │                │
//                   ▼                │
//                   └────────────────┘  inbound leg ──► fix (anchor)
//                                          ↑
//                                  ┌───────┴──────┐
//                                  │ inboundCourse│
//                                  └──────────────┘
//
// The fix sits at the END of the inbound leg. The inbound course points
// toward the fix from the hold area. Turn direction = R means RIGHT turns
// at the end of each leg (standard). Turn direction = L means LEFT turns
// (non-standard, less common).
//
// For Pattern A (low altitude, ≤ 14,000 ft MSL): turn radius ~1.0 NM at
// 175 kt + 25° bank. We approximate as 1.0 NM for the chart-overlay use case.
// Real-world width varies with airspeed but for visualization 1.0 NM is fine.

import { destinationPoint } from './geo';

export interface HoldingPattern {
  /** Fix lat in decimal degrees. */
  fixLat: number;
  /** Fix lon in decimal degrees. */
  fixLon: number;
  /** Inbound course in TRUE degrees (caller responsible for magnetic→true
   *  conversion if NASR-style magnetic input). */
  inboundCourseTrueDeg: number;
  /** 'R' (standard right turns) or 'L' (non-standard left turns). */
  turnDirection: 'L' | 'R';
  /** Outbound leg length:
   *   - { kind: 'time', minutes: 1 } → derived NM at standard 175 kt = ~2.9 NM
   *   - { kind: 'distance', nm: 5 } → fixed NM
   *   - When null/missing, defaults to 1 minute at standard speed.  */
  legLength?:
    | { kind: 'time'; minutes: number }
    | { kind: 'distance'; nm: number }
    | null;
}

/** Default turn radius (NM) for low-altitude holding pattern overlay. */
export const HOLD_TURN_RADIUS_NM = 1.0;

/** Convert a time-based leg to a distance estimate at 175 kt (FAA Pattern A,
 *  ≤ 14,000 ft) or 230 kt above that. Caller can pass `kt` to override. */
export function holdLegDistanceNm(
  legLength: HoldingPattern['legLength'],
  speedKt = 175,
): number {
  if (!legLength) return (speedKt * 1) / 60; // default 1 minute
  if (legLength.kind === 'distance') return legLength.nm;
  // time
  return (speedKt * legLength.minutes) / 60;
}

/**
 * Build a closed-ring GeoJSON polygon representing the racetrack boundary.
 *
 * The polygon is a (5–6 segments straight legs) + (32 segments per turn)
 * = ~70-vertex closed ring. Suitable for stroke-only display.
 *
 * Coordinate order is `[lon, lat]` per GeoJSON convention. The ring is
 * closed (first === last vertex).
 */
export function holdingPatternRing(
  hold: HoldingPattern,
  opts: {
    legLengthNm?: number;
    turnRadiusNm?: number;
    turnSegments?: number;
  } = {},
): Array<[number, number]> {
  const turnRadius = opts.turnRadiusNm ?? HOLD_TURN_RADIUS_NM;
  const turnSegments = opts.turnSegments ?? 32;
  const legNm = opts.legLengthNm ?? holdLegDistanceNm(hold.legLength);

  // Conventions in true-degrees compass space:
  // - inboundCourseTrueDeg points toward the fix from the holding area.
  // - The outbound leg is on the OPPOSITE side of the fix, parallel to
  //   the inbound course, offset by 2 * turnRadius perpendicular.
  // - For RIGHT turns (turnDirection='R'), the racetrack lies to the
  //   RIGHT of the inbound course (when looking along the inbound bearing).
  //   For LEFT turns, it's on the left.

  const inb = hold.inboundCourseTrueDeg;
  const outboundBrg = (inb + 180) % 360; // bearing pointing AWAY from fix
  const perpToHold = hold.turnDirection === 'R'
    ? (inb + 90) % 360   // 90° right of inbound = right side
    : (inb + 270) % 360; // 90° left of inbound

  // Center of turn 1 (the far-end-of-inbound-leg turn): start at fix and
  // go BACKWARDS along the inbound course (outbound direction) by `legNm`,
  // then offset perpendicular by `turnRadius`.
  // Wait - actually let's place the anchor as the FIX itself, with the
  // racetrack body extending OUTBOUND from the fix.
  //
  // Inbound-leg endpoints:
  //   A1 = fix (where inbound terminates)
  //   A2 = fix + outbound * legNm (where inbound starts after the turn)
  // Then offset both A1 and A2 perpendicular toward the racetrack body
  // to get the inbound-leg track? No - actually the FIX is AT the inbound
  // end. The inbound LINE is exactly along the inbound course toward the fix.
  //
  // So: inbound leg runs from A2 to A1 (= fix).
  // The OUTBOUND leg runs parallel, offset by 2*turnRadius perpendicular.
  // Turn 1 (at A1/fix end) is a 180° arc centered halfway between A1 and B1
  //   = at fix + perpToHold * turnRadius.
  // Turn 2 (at A2 end) is a 180° arc centered halfway between A2 and B2
  //   = at A2 + perpToHold * turnRadius.

  // Position of the inbound-leg far end (point A2):
  const A2 = destinationPoint(hold.fixLat, hold.fixLon, outboundBrg, legNm);
  // Centers of the two turns:
  const turn1Center = destinationPoint(
    hold.fixLat, hold.fixLon, perpToHold, turnRadius,
  );
  const turn2Center = destinationPoint(
    A2.lat, A2.lon, perpToHold, turnRadius,
  );
  // Outbound-leg endpoints:
  const B1 = destinationPoint(
    turn1Center.lat, turn1Center.lon, perpToHold, turnRadius,
  );
  const B2 = destinationPoint(
    turn2Center.lat, turn2Center.lon, perpToHold, turnRadius,
  );

  const ring: Array<[number, number]> = [];

  // Inbound leg: A2 -> A1 (= fix).
  ring.push([A2.lon, A2.lat]);
  ring.push([hold.fixLon, hold.fixLat]);

  // Turn 1: 180° arc from fix to B1 around turn1Center.
  // The arc goes from the "perpToHold + 180" side (fix side) sweeping
  // 180° through the outbound direction toward B1.
  // Start bearing (from turn1Center toward fix) = perpToHold + 180.
  // End bearing (from turn1Center toward B1) = perpToHold.
  // We sweep counter-clockwise if turnDirection='L', clockwise if 'R'.
  // In compass-bearing math, "right turn from north" is bearing increasing.
  const turn1StartBrg = (perpToHold + 180) % 360;
  const turn1EndBrg = perpToHold;
  const turnDir = hold.turnDirection === 'R' ? 1 : -1;
  for (let i = 1; i <= turnSegments; i++) {
    // Sweep 180° in turnDir direction
    const t = i / turnSegments;
    const brg = (turn1StartBrg + turnDir * 180 * t + 360) % 360;
    const p = destinationPoint(turn1Center.lat, turn1Center.lon, brg, turnRadius);
    ring.push([p.lon, p.lat]);
  }

  // Outbound leg: B1 -> B2.
  ring.push([B2.lon, B2.lat]);

  // Turn 2: 180° arc from B2 to A2 around turn2Center.
  const turn2StartBrg = perpToHold;
  for (let i = 1; i <= turnSegments; i++) {
    const t = i / turnSegments;
    const brg = (turn2StartBrg + turnDir * 180 * t + 360) % 360;
    const p = destinationPoint(turn2Center.lat, turn2Center.lon, brg, turnRadius);
    ring.push([p.lon, p.lat]);
  }

  // Close the ring (back to A2).
  ring.push([A2.lon, A2.lat]);
  return ring;
}

/**
 * Build a GeoJSON Feature for one holding pattern, suitable for
 * MapLibre `geojson` source.
 */
export function holdingPatternFeature(
  hold: HoldingPattern,
  properties: Record<string, unknown> = {},
  geometryOpts: Parameters<typeof holdingPatternRing>[1] = {},
): GeoJSON.Feature<GeoJSON.LineString> {
  const ring = holdingPatternRing(hold, geometryOpts);
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: ring },
    properties,
  };
}

// ── Procedure turn geometry ───────────────────────────────────────────────
//
// FAA TPP "45/180" procedure turn (PT) — the chart depiction is:
//
//   Fix ────────────────────────►  outbound on inbound-course-reciprocal
//                                  for ~1 min (~4 NM)
//                                  │
//                                  ╲ 45° turn AWAY from inbound (R PT =
//                                   ╲  barb on RIGHT side of inbound)
//                                    ╲
//                                     ╲ ~1 min straight on 45° barb heading
//                                      ╲
//                                       ╮  180° turn back toward
//                                       │  inbound centerline
//                                      ╱
//                                     ╱  intercept the inbound course
//                                    ╱   from the barb side
//   Fix ◄──────────────────────── ╱     final segment on inbound back
//                                       to the fix
//
// Convention: `turnDirection = 'R'` places the maneuvering area on the
// RIGHT side of the inbound course (i.e., barb points right when facing
// the fix along the inbound bearing). 'L' mirrors it to the left.
//
// For PT speeds (Cat A/B ~ 120 kt) at standard rate, turn radius ≈ 1 NM.

/** Default outbound leg distance (NM) — ~1 min at 240 kt or 2 min at 120 kt. */
export const PT_OUTBOUND_LEG_NM = 4.0;
/** Default 45° barb leg distance (NM) — same as outbound by FAA convention. */
export const PT_BARB_LEG_NM = 4.0;
/** Default turn radius (NM) for PT arcs at Cat A/B speed. */
export const PT_TURN_RADIUS_NM = 1.0;

export interface ProcedureTurn {
  /** Fix lat in decimal degrees. */
  fixLat: number;
  /** Fix lon in decimal degrees. */
  fixLon: number;
  /** Inbound course in TRUE degrees (caller responsible for magnetic→true
   *  conversion). Points toward the fix from outside. */
  inboundCourseTrueDeg: number;
  /** 'R' = maneuvering on right side of inbound (initial 45° turn from the
   *  outbound recip is LEFT). 'L' = mirror image. */
  turnDirection: 'L' | 'R';
}

/**
 * Solve the analytic intercept distance from `p` along bearing `headingDeg`
 * to the line through `fix` with bearing `inbDeg`. Local flat-earth frame
 * (accurate to << 0.1 NM at PT distances). Returns NM, clamped to >= 0 and
 * capped to a sane upper bound so degenerate input doesn't blow up the ring.
 */
function interceptDistanceNm(
  fixLat: number, fixLon: number,
  pLat: number, pLon: number,
  inbDeg: number, headingDeg: number,
): number {
  // Local ENU frame, x = east-NM, y = north-NM.
  const dLatNm = (pLat - fixLat) * 60;
  const dLonNm = (pLon - fixLon) * 60 * Math.cos((fixLat * Math.PI) / 180);
  const toRad = (d: number) => (d * Math.PI) / 180;
  const ui = { x: Math.sin(toRad(inbDeg)), y: Math.cos(toRad(inbDeg)) };
  const uh = { x: Math.sin(toRad(headingDeg)), y: Math.cos(toRad(headingDeg)) };
  // (px + t*uh.x) * ui.y - (py + t*uh.y) * ui.x = 0
  //   → t = (py * ui.x - px * ui.y) / (uh.x * ui.y - uh.y * ui.x)
  const denom = uh.x * ui.y - uh.y * ui.x;
  if (Math.abs(denom) < 1e-9) return 0; // parallel — no intercept
  const t = (dLatNm * ui.x - dLonNm * ui.y) / denom;
  if (!Number.isFinite(t) || t <= 0) return 0;
  return Math.min(t, 20);
}

/**
 * Build a procedure-turn path as an [lon, lat] vertex list. Starts at the
 * fix, traces the standard 45/180 maneuver, and returns to the fix.
 *
 * Geometry (FAA TPP):
 *   1. Outbound from fix on the inbound-course-reciprocal for `outboundLegNm`.
 *   2. 45° turn AWAY from inbound (R = right of inbound, L = left).
 *   3. 1-min straight on the 45° outbound heading (length `barbLegNm`).
 *   4. 180° turn back toward the centerline (opposite of the initial turn).
 *   5. Intercept the inbound course line (analytic intersection).
 *   6. Final segment on the inbound course back to the fix.
 *
 * Magnetic-vs-true: pass true bearing; magnetic→true conversion is a
 * separate concern (per-fix magvar). At PT chart scale (<= 10 NM) the
 * < 1° offset is invisible.
 */
export function procedureTurnRing(
  pt: ProcedureTurn,
  opts: {
    outboundLegNm?: number;
    barbLegNm?: number;
    turnRadiusNm?: number;
    turnSegments?: number;
  } = {},
): Array<[number, number]> {
  const outboundLegNm = opts.outboundLegNm ?? PT_OUTBOUND_LEG_NM;
  const barbLegNm = opts.barbLegNm ?? PT_BARB_LEG_NM;
  const turnRadius = opts.turnRadiusNm ?? PT_TURN_RADIUS_NM;
  // 32 segments per 180° turn matches holdingPatternRing's smoothness.
  const turnSegments = opts.turnSegments ?? 32;
  const arc45Segments = Math.max(2, Math.round(turnSegments / 4));

  const inb = pt.inboundCourseTrueDeg;
  const outRecip = (inb + 180) % 360;
  // R PT → barb on right side of inbound. When flying outbound (recip), the
  // right side of inbound is to the AIRCRAFT's left, so initial 45° turn is
  // LEFT (CCW, heading decreases). L PT mirrors to RIGHT (CW, increases).
  const initialSign = pt.turnDirection === 'R' ? -1 : +1;
  const pt45Brg = (outRecip + initialSign * 45 + 360) % 360;
  const reversalSign = -initialSign;
  const afterBarbBrg = (pt45Brg + reversalSign * 180 + 360) % 360;

  const ring: Array<[number, number]> = [];

  // 0. Fix.
  ring.push([pt.fixLon, pt.fixLat]);

  // 1. Outbound leg → P1.
  const P1 = destinationPoint(pt.fixLat, pt.fixLon, outRecip, outboundLegNm);
  ring.push([P1.lon, P1.lat]);

  // 2. 45° turn arc at P1. Turn center is perpendicular to current heading
  // in the direction of the turn.
  const perp1 = (outRecip + initialSign * 90 + 360) % 360;
  const t1Center = destinationPoint(P1.lat, P1.lon, perp1, turnRadius);
  const arc1Start = (perp1 + 180) % 360; // bearing from center toward P1
  for (let i = 1; i <= arc45Segments; i++) {
    const f = i / arc45Segments;
    const brg = (arc1Start + initialSign * 45 * f + 360) % 360;
    const p = destinationPoint(t1Center.lat, t1Center.lon, brg, turnRadius);
    ring.push([p.lon, p.lat]);
  }
  const P2 = destinationPoint(
    t1Center.lat, t1Center.lon,
    (arc1Start + initialSign * 45 + 360) % 360,
    turnRadius,
  );

  // 3. 45° straight leg → P3.
  const P3 = destinationPoint(P2.lat, P2.lon, pt45Brg, barbLegNm);
  ring.push([P3.lon, P3.lat]);

  // 4. 180° turn arc at P3 (reverse direction).
  const perp2 = (pt45Brg + reversalSign * 90 + 360) % 360;
  const t2Center = destinationPoint(P3.lat, P3.lon, perp2, turnRadius);
  const arc2Start = (perp2 + 180) % 360;
  for (let i = 1; i <= turnSegments; i++) {
    const f = i / turnSegments;
    const brg = (arc2Start + reversalSign * 180 * f + 360) % 360;
    const p = destinationPoint(t2Center.lat, t2Center.lon, brg, turnRadius);
    ring.push([p.lon, p.lat]);
  }
  const P4 = destinationPoint(
    t2Center.lat, t2Center.lon,
    (arc2Start + reversalSign * 180 + 360) % 360,
    turnRadius,
  );

  // 5. Intercept the inbound course line, fly to P5 on the centerline.
  const interceptNm = interceptDistanceNm(
    pt.fixLat, pt.fixLon, P4.lat, P4.lon, inb, afterBarbBrg,
  );
  if (interceptNm > 0) {
    const P5 = destinationPoint(P4.lat, P4.lon, afterBarbBrg, interceptNm);
    ring.push([P5.lon, P5.lat]);
  }

  // 6. Inbound back to fix (closes the ring).
  ring.push([pt.fixLon, pt.fixLat]);

  return ring;
}

/**
 * Build a GeoJSON Feature for one procedure turn.
 */
export function procedureTurnFeature(
  pt: ProcedureTurn,
  properties: Record<string, unknown> = {},
  geometryOpts: Parameters<typeof procedureTurnRing>[1] = {},
): GeoJSON.Feature<GeoJSON.LineString> {
  const ring = procedureTurnRing(pt, geometryOpts);
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: ring },
    properties,
  };
}

// ── Holding pattern ENTRY procedure ───────────────────────────────────────────
//
// AIM 5-3-8 entry sector classification, with helpers to render the entry
// arc on the map. Useful when we know the aircraft's heading when crossing
// the holding fix (e.g. derived from the previous flight-plan leg's track).
//
// Standard right-turn hold, inbound bearing B (the radial pointing toward
// the fix from the holding-side body):
//
//   Direct entry sector:   from (B+110) CW to (B+250) CW, i.e. spans 180°
//                          centered on the inbound bearing. The aircraft
//                          is flying generally toward the fix from any
//                          direction within 70° of the inbound; turn
//                          right and join the holding pattern.
//   Teardrop entry:        from (B+250) CW to (B+360) (i.e. (B+250)..(B-0))
//                          — 70° wide. Aircraft is arriving from the
//                          holding-side of the inbound. Fly outbound on
//                          a 30° offset, then turn back to join inbound.
//   Parallel entry:        from (B-110) CCW to (B-250) CCW = the remaining
//                          110° sector. Aircraft is arriving on the
//                          NON-holding-side of the inbound. Fly outbound
//                          parallel to the holding course, then make a
//                          turn IN THE DIRECTION OF HOLDING through > 180°
//                          to intercept the inbound.
//
// For a left-turn (non-standard) hold, mirror everything around the
// inbound axis.
//
// Reference: AIM 5-3-8, ICAO Doc 8168 Vol II Part I Section 4 Chapter 1.

export type HoldEntryType = 'direct' | 'teardrop' | 'parallel';

export interface HoldEntryArrival {
  /** Aircraft heading when arriving at the fix, true degrees (caller must
   *  convert from magnetic if upstream is magnetic). */
  arrivalHeadingTrueDeg: number;
}

/**
 * Classify the AIM entry type for an aircraft arriving at the holding fix.
 */
export function classifyHoldEntry(
  hold: HoldingPattern,
  arrival: HoldEntryArrival,
): HoldEntryType {
  const inb = hold.inboundCourseTrueDeg;
  const hdg = arrival.arrivalHeadingTrueDeg;
  // Compute angle FROM inbound TO arrival heading, normalized to (-180, 180].
  let rel = ((hdg - inb + 540) % 360) - 180;
  // For a RIGHT-turn hold, the holding side is to the RIGHT of inbound.
  // The teardrop sector is on the HOLDING SIDE of the inbound axis.
  // For LEFT-turn hold, flip the sign of `rel`.
  if (hold.turnDirection === 'L') {
    rel = -rel;
  }
  // Now `rel` is the signed angle from inbound:
  //   positive = holding side
  //   negative = non-holding side
  // Direct entry: within 70° either side of inbound on the holding side,
  // OR within 110° on the non-holding side.
  // Wait — the AIM sectors are described relative to the OUTBOUND bearing,
  // not the inbound bearing. Let me re-anchor:
  //   - Direct: the half-plane CENTERED on the inbound radial (180° wide),
  //     extending 70° to one side and 110° to the other.
  //   - For a RIGHT hold, the 70° portion is on the HOLDING side (right)
  //     and the 110° portion is on the NON-holding side (left).
  //   - Teardrop: the 70° sector on the holding side OPPOSITE the direct
  //     half-plane (i.e. the holding-side back-arc).
  //   - Parallel: the 110° sector on the non-holding side OPPOSITE the
  //     direct half-plane (i.e. the non-holding-side back-arc).
  //
  // Translating to `rel` (signed angle from INBOUND, positive = holding side):
  //   - Direct half-plane = the 180° centered on rel=0:
  //       (rel between -110° and +70°, inclusive)
  //   - Teardrop = (rel between +70° and +180°)         [holding side back]
  //   - Parallel = (rel between -180° and -110°)         [non-holding back]
  if (rel > 70 && rel <= 180) return 'teardrop';
  if (rel < -110 && rel >= -180) return 'parallel';
  return 'direct';
}

/**
 * Build a GeoJSON LineString showing the AIM-recommended ENTRY path into
 * the holding pattern, given the aircraft's arrival heading.
 *
 * Vertex sequence:
 *   [arrival_anchor]                    — a point ~3 NM short of the fix
 *                                          along the aircraft's arrival
 *                                          bearing, so we can render the
 *                                          inbound segment.
 *   [fix]                               — the hold fix itself.
 *   [entry_arc_or_path...]              — entry geometry depending on type
 *   [end_at_fix_for_first_inbound]      — the path ends back at the fix
 *                                          (i.e. "first time crossing the
 *                                          fix on the inbound course of
 *                                          the hold itself").
 *
 * The returned ring is NOT closed (it's an arrival + entry path, not a
 * racetrack boundary).
 */
export function holdEntryPath(
  hold: HoldingPattern,
  arrival: HoldEntryArrival,
  opts: {
    turnRadiusNm?: number;
    arrivalLeadInNm?: number;
    legLengthNm?: number;
  } = {},
): {
  type: HoldEntryType;
  path: Array<[number, number]>;
} {
  const turnRadius = opts.turnRadiusNm ?? HOLD_TURN_RADIUS_NM;
  const arrivalLeadIn = opts.arrivalLeadInNm ?? 3.0;
  const legNm = opts.legLengthNm ?? holdLegDistanceNm(hold.legLength);
  const type = classifyHoldEntry(hold, arrival);
  const inb = hold.inboundCourseTrueDeg;
  const outboundBrg = (inb + 180) % 360;
  const arrivalBrg = arrival.arrivalHeadingTrueDeg;
  const path: Array<[number, number]> = [];

  // 1. Arrival lead-in: render the inbound segment ~3 NM back from the fix
  //    along the RECIPROCAL of the arrival heading. This shows the pilot
  //    where they're coming from.
  const reciprocalArrival = (arrivalBrg + 180) % 360;
  const arrivalStart = destinationPoint(
    hold.fixLat, hold.fixLon, reciprocalArrival, arrivalLeadIn,
  );
  path.push([arrivalStart.lon, arrivalStart.lat]);
  path.push([hold.fixLon, hold.fixLat]);

  // perpToHold: 90° toward the holding-pattern body.
  const perpToHold = hold.turnDirection === 'R'
    ? (inb + 90) % 360
    : (inb + 270) % 360;
  const reversalSign = hold.turnDirection === 'R' ? 1 : -1;
  const turnSegments = 24;

  if (type === 'direct') {
    // Direct entry: turn from arrival heading INTO the hold's outbound
    // direction via a turn in the holding direction. Render as an arc
    // from the fix bearing (arrivalBrg) sweeping through (outboundBrg).
    // The arc center is `turnRadius` perpendicular toward the hold side,
    // measured from the fix.
    const arcCenter = destinationPoint(
      hold.fixLat, hold.fixLon, perpToHold, turnRadius,
    );
    // Starting bearing from the center toward the fix:
    const arcStartFromCenter = (perpToHold + 180) % 360;
    // We sweep in the holding-direction toward the outbound side.
    // Heuristic sweep amount: from start-bearing toward the outbound side,
    // total ~180° to reach the outbound endpoint of the inbound leg's
    // racetrack start position.
    for (let i = 1; i <= turnSegments; i++) {
      const t = i / turnSegments;
      const brg = (arcStartFromCenter + reversalSign * 180 * t + 360) % 360;
      const p = destinationPoint(arcCenter.lat, arcCenter.lon, brg, turnRadius);
      path.push([p.lon, p.lat]);
    }
    // End at the outbound-leg start point (which is also the racetrack's
    // turn-2 start point in the hold body).
    const outboundLegStart = destinationPoint(
      arcCenter.lat, arcCenter.lon, perpToHold, turnRadius,
    );
    path.push([outboundLegStart.lon, outboundLegStart.lat]);
    // Outbound leg back to the corresponding far point.
    const outboundLegEnd = destinationPoint(
      outboundLegStart.lat, outboundLegStart.lon, outboundBrg, legNm,
    );
    path.push([outboundLegEnd.lon, outboundLegEnd.lat]);
  } else if (type === 'teardrop') {
    // Teardrop entry per FAA AIM 5-3-7:
    //   1. At the fix, turn to a heading 30° offset from outbound TOWARD
    //      the holding side (same side as the racetrack body).
    //   2. Fly that offset for one leg length → teardropEnd.
    //   3. Turn in the hold's primary turn direction back to intercept
    //      the inbound course on the inbound radial.
    //   4. Fly inbound to the fix.
    //
    // For a clean visualization we want a single circular arc from
    // teardropEnd that lands TANGENT on the inbound radial, heading
    // inbound. Given the 30° offset toward the holding side, this
    // constrains both the sweep (210° in the primary turn direction —
    // a 180° reversal plus the 30° offset, which is on the SAME side as
    // the turn) and the arc radius:
    //
    //   r_arc = legNm / (2 + √3) ≈ 0.268 × legNm
    //
    // Derivation in the local fix frame: teardropEnd is at perpendicular
    // distance d = legNm·cos(60°) = 0.5·legNm from the inbound radial on
    // the holding side. The arc center must lie on (a) the perpendicular
    // to teardrop-outbound at teardropEnd on the holding-side AND (b)
    // distance r_arc from the inbound radial on the holding side. The
    // angle between those two perpendiculars is 60°, yielding
    //   r_arc = d / (1 + sin(60°)) = (0.5·legNm) / (1 + √3/2)
    //         = legNm / (2 + √3).

    // 30° offset toward the holding side from outbound.
    // R-hold (body right of inbound): outbound − 30°.
    // L-hold (body left of inbound):  outbound + 30°.
    const teardropOutbound =
      hold.turnDirection === 'R'
        ? (outboundBrg - 30 + 360) % 360
        : (outboundBrg + 30) % 360;
    const teardropEnd = destinationPoint(
      hold.fixLat, hold.fixLon, teardropOutbound, legNm,
    );
    path.push([teardropEnd.lon, teardropEnd.lat]);

    const arcRadius = legNm / (2 + Math.sqrt(3));
    // Turn-center perpendicular: 90° from the heading on the side of the
    // primary turn direction. R-hold turns right → center is right of
    // heading. L-hold turns left → center is left of heading.
    const teardropArcPerp =
      hold.turnDirection === 'R'
        ? (teardropOutbound + 90) % 360
        : (teardropOutbound + 270) % 360;
    const teardropArcCenter = destinationPoint(
      teardropEnd.lat, teardropEnd.lon, teardropArcPerp, arcRadius,
    );
    // Bearing from center toward teardropEnd is the reciprocal of the
    // turn-center perpendicular; we sweep 210° in the primary direction.
    const teardropArcStartBrg = (teardropArcPerp + 180) % 360;
    for (let i = 1; i <= turnSegments; i++) {
      const t = i / turnSegments;
      const brg = (teardropArcStartBrg + reversalSign * 210 * t + 360) % 360;
      const p = destinationPoint(
        teardropArcCenter.lat, teardropArcCenter.lon, brg, arcRadius,
      );
      path.push([p.lon, p.lat]);
    }
    // Arc ends ON the inbound radial heading inbound. Final segment is
    // a clean inbound-leg from there to the fix.
    path.push([hold.fixLon, hold.fixLat]);
  } else {
    // Parallel entry: fly outbound on a heading PARALLEL to the holding
    // course (i.e. on the outbound bearing) for ~1 minute on the
    // NON-holding side. Then turn IN the holding direction through > 210°
    // to intercept the inbound radial.
    const parallelStart = destinationPoint(
      hold.fixLat, hold.fixLon, outboundBrg, legNm,
    );
    path.push([parallelStart.lon, parallelStart.lat]);
    // Turn center is on the HOLDING SIDE of the parallel-leg end.
    const parallelTurnCenter = destinationPoint(
      parallelStart.lat, parallelStart.lon, perpToHold, turnRadius,
    );
    const parallelArcStart = (perpToHold + 180) % 360;
    // > 210° sweep in the holding direction. We use 225° to be on the
    // generous side so the curve clearly shows passing through the
    // inbound radial.
    const totalSweepDeg = 225;
    for (let i = 1; i <= turnSegments; i++) {
      const t = i / turnSegments;
      const brg = (parallelArcStart + reversalSign * totalSweepDeg * t + 360) % 360;
      const p = destinationPoint(
        parallelTurnCenter.lat, parallelTurnCenter.lon, brg, turnRadius,
      );
      path.push([p.lon, p.lat]);
    }
    // Final intercept to the fix.
    path.push([hold.fixLon, hold.fixLat]);
  }

  return { type, path };
}

/**
 * Build a GeoJSON Feature for the recommended hold entry. The feature
 * `properties.entryType` is one of 'direct' | 'teardrop' | 'parallel'
 * so paint expressions can color-code each entry style.
 */
export function holdEntryFeature(
  hold: HoldingPattern,
  arrival: HoldEntryArrival,
  properties: Record<string, unknown> = {},
  geometryOpts: Parameters<typeof holdEntryPath>[2] = {},
): GeoJSON.Feature<GeoJSON.LineString> {
  const { type, path } = holdEntryPath(hold, arrival, geometryOpts);
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: path },
    properties: { ...properties, entryType: type },
  };
}
